import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import { Sort } from "@mui/icons-material";
import ActionFilter from "../../components/ActionFilter";
import dayjs from "dayjs";
import { limitChar } from "../../utils/common";
import { useNavigate } from "react-router-dom";
import routePaths from "../../router/route-paths";
import { useDispatch } from "react-redux";
import { deleteDance, getDanceOrder, listDance, updateDanceOrder } from "../../http/dance";
import { setLoading, showToast } from "../../store/slices/common";
import { LadderChip } from "../../components/FormElements/LadderField";
import LiveAvailabilityCard from "../../components/LiveAvailabilityCard";
import { DANCE_FORMATS, danceFormatFromPlayModes, danceFormatLabel } from "./danceHelpers";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import AvailabilityModal from "./AvailabilityModal";
import PageLayout from "../../components/PageLayout";
import CommonPagination from "../../components/Table/CommonPagination";
import TrackOrderModal from "../../components/TrackOrderModal";

const PAGE_SIZE = 20;

const fetchDanceOrder = async () => {
  const res = await getDanceOrder();
  return (res?.data ?? res)?.dances ?? [];
};

// Server-side availability-status filters (match backend `status` param values)
const STATUS_FILTERS = [
  { value: "", label: "All", color: "primary" },
  { value: "upcoming", label: "Upcoming", color: "warning" },
  { value: "tbd", label: "Date TBD", color: "default" },
  { value: "active", label: "Active", color: "success" },
  { value: "always", label: "Always", color: "success" },
  { value: "expired", label: "Expired", color: "default" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  dialogContent: {
    maxWidth: "100%",
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    maxHeight: "calc(100vh - 135px)",
    minHeight: "calc(100vh - 135px)",
    boxShadow: "none !important",
  },
  table: {
    minWidth: "100%",
    border: "1px solid rgba(224, 224, 224, 1)",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  actionsCell: {
    textAlign: "center",
  },
  actionsHeader: {
    textAlign: "center",
    fontWeight: "bold",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  searchField: {
    width: "300px",
    "& .MuiInputLabel-root": {
      color: "#142C5C",
      borderRadius: "15px",
      height: "50px",
    },
    "& .MuiInputBase-formControl": {
      padding: "15px",
      display: "flex",
      alignItems: "center",
      "& input": {
        padding: 0,
      },
      "& fieldset": {
        borderColor: "#142C5C",
      },
      "&:hover fieldset": {
        borderColor: "#142C5C",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C",
      },
    },
  },
  input: {
    marginBottom: "0 !important",
    "& label.Mui-focused": {
      color: "#142C5C",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#142C5C",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderColor: "#142C5C",
      },
      "&:hover fieldset": {
        borderColor: "#142C5C",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C",
      },
    },
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    borderColor: "#142C5C !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "10px",
  },
}));

const getAvailabilityStatus = (item) => {
  const now = new Date();
  const start = item.availabilityStartTime ? new Date(item.availabilityStartTime) : null;
  const end = item.availabilityEndTime ? new Date(item.availabilityEndTime) : null;

  if (item.isUnscheduled) return { label: "Date TBD", color: "default" };
  if (!start && !end) return { label: "Always", color: "success" };
  if (start && now < start) return { label: "Scheduled", color: "warning" };
  if (end && now > end) return { label: "Expired", color: "default" };
  return { label: "Active", color: "success" };
};

const ManageDance = () => {
  const classes = useStyles();
  const [dances, setDances] = useState([]);
  const [deleteId, setDeleteId] = useState("");
  const [availabilityDance, setAvailabilityDance] = useState(null);
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0); // 0-based, CommonPagination convention
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [, setTick] = useState(0);
  // sortBy "" = server default (priority order: Upcoming → TBD → Active → Always → Expired)
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.removeItem("editDance");
  }, []);

  const fetchDancesHandler = async () => {
    try {
      dispatch(setLoading(true));
      const res = await listDance({
        limit: PAGE_SIZE,
        page: page + 1, // backend pages are 1-based
        searchBy: debouncedSearch || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortBy ? sortOrder : undefined,
        status: statusFilter || undefined,
      });
      // Handle multiple response shapes (same pattern as drawing prompts)
      const data = res?.data ?? res;
      let dancesList = [];
      if (Array.isArray(data)) {
        dancesList = data;
      } else if (data?.dances) {
        dancesList = data.dances;
      } else if (data?.list) {
        dancesList = data.list;
      } else if (data && typeof data === "object" && !Array.isArray(data)) {
        dancesList = Array.isArray(data.data) ? data.data : [];
      }
      setDances(dancesList);
      setTotal(data?.total ?? res?.total ?? dancesList.length);
    } catch (err) {
      console.error("Error fetching dance tracks:", err);
      dispatch(
        showToast({
          color: "error",
          msg: err?.response?.data?.message || err?.message || "Failed to load dance tracks",
        })
      );
      setDances([]);
      setTotal(0);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchDancesHandler();
  }, [page, debouncedSearch, sortBy, sortOrder, statusFilter, refreshKey]);

  const refetchList = () => setRefreshKey((k) => k + 1);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Debounce search
    clearTimeout(window.danceSearchTimeout);
    window.danceSearchTimeout = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(value);
    }, 300);
  };

  const handleStatusFilter = (value) => {
    setPage(0);
    setStatusFilter((prev) => (prev === value ? "" : value));
  };

  const openAddDancePage = () => {
    navigate(routePaths.uploadDance);
  };

  const editHandler = (dance) => {
    localStorage.setItem("editDance", JSON.stringify(dance));
    navigate(routePaths.uploadDance);
  };

  const deleteHandler = async (dance) => {
    setDeleteId(dance?._id);
  };

  const confirmDeleteHandler = async () => {
    try {
      dispatch(setLoading(true));
      await deleteDance(deleteId);
      setDeleteId("");
      // If the last row of a page was deleted, step back a page.
      if (dances.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        refetchList();
      }
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const cancelDeleteHandler = () => {
    setDeleteId("");
  };

  const updateAvailabilityHandler = (dance) => {
    setAvailabilityDance(dance);
  };

  const closeAvailabilityModal = () => {
    setAvailabilityDance(null);
  };

  const onAvailabilitySuccess = () => {
    refetchList();
  };

  // Server-side sort: 1st click = desc, 2nd = asc, 3rd = back to priority order
  const handleSort = (field) => {
    setPage(0);
    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("");
      setSortOrder("desc");
    }
  };

  return (
    <PageLayout
      title="Dance"
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setIsOrderOpen(true)}
            sx={{
              borderColor: "#142C5C",
              color: "#142C5C",
              borderRadius: "6px",
              fontSize: { xs: "12px", sm: "13px", lg: "14px" },
              height: { xs: "35px", sm: "40px" },
              "&:hover": { borderColor: "#0f2247", backgroundColor: "rgba(20, 44, 92, 0.04)" },
            }}
          >
            App Order
          </Button>
          <Button
            sx={{
              padding: {
                xs: "6px",
                sm: "6px 16px",
                md: "6px 16px",
                lg: "6px 16px",
                xl: "6px 16px",
              },
              backgroundColor: "#142C5C",
              color: "#fff",
              borderColor: "#0f2247",
              "&:hover": {
                backgroundColor: "#0f2247 !important",
              },
              borderRadius: "6px",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "13px",
                lg: "14px",
                xl: "14px",
              },
              height: {
                xs: "35px",
                sm: "40px",
                md: "40px",
                lg: "40px",
                xl: "40px",
              },
            }}
            variant="contained"
            onClick={openAddDancePage}
          >
            Add Dance Track
          </Button>
        </Box>
      }
      search={
        <>
          <TextField
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            sx={{
              width: "200px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: "#142C5C",
                },
                "&:hover fieldset": {
                  borderColor: "#142C5C",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#142C5C",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#142C5C" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant={sortBy === "availabilityStartTime" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSort("availabilityStartTime")}
              startIcon={<Sort />}
              sx={{
                backgroundColor: sortBy === "availabilityStartTime" ? "#142C5C" : "transparent",
                color: sortBy === "availabilityStartTime" ? "white" : "#142C5C",
                borderColor: "#142C5C",
                borderRadius: "4px",
                padding: "2px 6px",
                height: "32px",
                minWidth: "auto",
                fontSize: "10px",
                "&:hover": {
                  backgroundColor: sortBy === "availabilityStartTime" ? "#0f2247" : "rgba(20, 44, 92, 0.04)",
                  borderColor: "#0f2247",
                },
              }}
            >
              Start {sortBy === "availabilityStartTime" && (sortOrder === "desc" ? "↓" : "↑")}
            </Button>
            <Button
              variant={sortBy === "availabilityEndTime" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleSort("availabilityEndTime")}
              startIcon={<Sort />}
              sx={{
                backgroundColor: sortBy === "availabilityEndTime" ? "#142C5C" : "transparent",
                color: sortBy === "availabilityEndTime" ? "white" : "#142C5C",
                borderColor: "#142C5C",
                borderRadius: "4px",
                padding: "2px 6px",
                height: "32px",
                minWidth: "auto",
                fontSize: "10px",
                "&:hover": {
                  backgroundColor: sortBy === "availabilityEndTime" ? "#0f2247" : "rgba(20, 44, 92, 0.04)",
                  borderColor: "#0f2247",
                },
              }}
            >
              End {sortBy === "availabilityEndTime" && (sortOrder === "desc" ? "↓" : "↑")}
            </Button>
          </Box>
        </>
      }
    >
      <>
        <Box sx={{ marginBottom: "20px" }}>
          <LiveAvailabilityCard
            title="Dance Live Availability"
            featureLabel="Live dance pagents"
            endpoint="/admin/settings/dance-live-availability"
            responseKey="danceLiveAvailability"
            showNotificationsToggle
            scheduleHelp="While a window is open, tracks marked Live can be battled live. Tracks marked Video can still be recorded at the same time. Outside a window, only recorded videos are available — live-only tracks wait for the next window. Set Live and video, Video only, or Live only on each track below."
          />
        </Box>
        <Box
          sx={{
            padding: "15px 15px 0 15px",
            borderRadius: "6px",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", pb: "12px" }}>
            {STATUS_FILTERS.map((option) => (
              <Chip
                key={option.value || "all"}
                label={option.label}
                size="small"
                clickable
                color={statusFilter === option.value ? option.color : "default"}
                variant={statusFilter === option.value ? "filled" : "outlined"}
                onClick={() => handleStatusFilter(option.value)}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Thumbnail
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Title
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    Submissions
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Start Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    End Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    Availability
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    Mode
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dances?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        textAlign: "center",
                        height: "calc(100vh - 270px)",
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      No dance tracks found
                    </TableCell>
                  </TableRow>
                ) : (
                  dances.map((dance) => (
                    <TableRow key={dance?._id}>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        <img
                          src={dance?.coverImage}
                          alt="cover"
                          style={{
                            paddingLeft: "20px",
                            height: "50px",
                            width: "50px",
                            objectFit: "cover",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        {limitChar(dance?.title)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                          textAlign: "center",
                        }}
                      >
                        {dance?.activeSubmissions || 0}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        {dance?.availabilityStartTime
                          ? dayjs(dance.availabilityStartTime).format("DD MMM YYYY - h:mm A")
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        {dance?.availabilityEndTime
                          ? dayjs(dance.availabilityEndTime).format("DD MMM YYYY - h:mm A")
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                          textAlign: "center",
                        }}
                      >
                        {(() => {
                          const status = getAvailabilityStatus(dance);
                          return (
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                              variant={status.label === "Expired" ? "outlined" : "filled"}
                              sx={{ fontWeight: 500, fontSize: "12px" }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                          <LadderChip
                            ladder={dance?.ladder}
                            ladderLocked={dance?.ladderLocked}
                            liveOnly={
                              danceFormatFromPlayModes(dance?.playModes) === DANCE_FORMATS.LIVE_ONLY
                            }
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={danceFormatLabel(danceFormatFromPlayModes(dance?.playModes))}
                          />
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                          paddingLeft: {
                            xs: "5px",
                            sm: "10px",
                            md: "10px",
                            lg: "15px",
                            xl: "15px",
                          },
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            top: "20%",
                          }}
                        >
                          <ActionFilter
                            index={dance}
                            onEdit={editHandler}
                            onDelete={deleteHandler}
                            onUpdateAvailability={updateAvailabilityHandler}
                            dance={true}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <Box sx={{ py: 1.5 }}>
              <CommonPagination
                rowsPerPage={PAGE_SIZE}
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
              />
            </Box>
          )}
        </Box>
        <ToastContainer />
        {deleteId && (
          <ConfirmDeleteModal
            onAccept={confirmDeleteHandler}
            onReject={cancelDeleteHandler}
            dance={true}
          />
        )}
        <AvailabilityModal
          open={!!availabilityDance}
          onClose={closeAvailabilityModal}
          dance={availabilityDance}
          onSuccess={onAvailabilitySuccess}
        />
        <TrackOrderModal
          open={isOrderOpen}
          onClose={() => setIsOrderOpen(false)}
          onSuccess={() => dispatch(showToast({ color: "success", msg: "Dance order updated" }))}
          title="Dance Order in App"
          fetchTracks={fetchDanceOrder}
          saveOrder={updateDanceOrder}
        />
      </>
    </PageLayout>
  );
};

export default ManageDance;
